import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Car } from "lucide-react";

const formatPrice = (price) => {
  if (!price) return "Consultar";
  const n = parseFloat(String(price).replace(/[,$]/g, ""));
  return `$${n.toLocaleString("es-MX")}`;
};

// Slugify (mismo criterio que backend `_slugify`).
const slugify = (t) => String(t || "").toLowerCase()
  .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Genera path SEO-friendly cuando estamos en el subdominio del catálogo.
const vehicleHref = (v, businessSlug, isRental) => {
  const suffix = isRental ? "?type=rental" : "";
  if (typeof window === "undefined") return `/catalogo/${businessSlug}/${v.id}${suffix}`;
  const host = window.location.hostname.toLowerCase();
  const isCatalogSub = host.startsWith("automotriz") || host === "automotriz";
  if (isCatalogSub && !isRental && v.brand && v.model_name) {
    return `/${slugify(v.brand)}/${slugify(v.model_name)}`;
  }
  return `/catalogo/${businessSlug}/${v.id}${suffix}`;
};

const API = process.env.REACT_APP_BACKEND_URL;

const pickImage = (v) => {
  // Preferimos web_image_url (endpoint cacheable) sobre web_image base64 (payload pesado)
  if (v.web_image_url) return `${API}${v.web_image_url}`;
  if (v.web_image) return v.web_image;
  if (v.gallery_images?.length) {
    const hook = v.gallery_images.find((i) => i.name === "Hook");
    if (hook?.url) return hook.url;
    return v.gallery_images[0]?.url || null;
  }
  return v.vehicle_image || null;
};

const Card = React.memo(function Card({ vehicle, index, businessSlug, isRental, accentColor }) {
  const img = pickImage(vehicle);
  const isGrouped = !!vehicle._grouped;
  const versionsCount = vehicle._versionsCount || 1;
  const showFromLabel = isGrouped && !isRental;
  const price = isRental ? vehicle.rental_price : vehicle.price;
  const priceDisplay = isRental
    ? (price ? `$${parseInt(price).toLocaleString("es-MX")}/mes` : "Consultar")
    : formatPrice(price);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 6) * 0.06 }}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <Link
        to={vehicleHref(vehicle, businessSlug, isRental)}
        className="group relative block rounded-2xl overflow-hidden bg-white border border-[#0B1623]/8 hover:border-[#0B1623]/25 shadow-[0_2px_8px_rgba(11,22,35,0.04)] hover:shadow-[0_20px_50px_rgba(11,22,35,0.10)] transition-all duration-500"
      >
        {/* Image */}
        <div className="relative aspect-[4/3] bg-[#F5F5F5] overflow-hidden">
          {img ? (
            <img
              src={img}
              alt={`${vehicle.brand} ${vehicle.model_name}`}
              className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-[1.06]"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Car className="w-14 h-14 text-[#0B1623]/20" />
            </div>
          )}

          {isRental && (
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#F09A21] text-white text-[11px] font-rajdhani font-semibold tracking-[0.15em] uppercase">
              Renta
            </div>
          )}

          {isGrouped && versionsCount > 1 && (
            <div
              className="absolute top-4 right-4 px-3 py-1 rounded-full text-white text-[11px] font-rajdhani font-semibold tracking-[0.15em] uppercase backdrop-blur-md"
              style={{ backgroundColor: `${accentColor}D5` }}
              data-testid={`versions-badge-${vehicle.id}`}
            >
              {versionsCount} versiones
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-5 md:p-6">
          <p className="font-rajdhani font-medium text-[#3B82F6] text-xs tracking-[0.3em] uppercase mb-2">
            {vehicle.brand || "Vehículo"}
          </p>
          <h3 className="font-rajdhani font-semibold text-[#0B1623] text-xl md:text-2xl leading-tight tracking-tight">
            {vehicle.model_name}
          </h3>
          <p className="font-manrope text-[#0B1623]/55 text-sm mt-1">
            {isGrouped
              ? (vehicle.year || "")
              : `${vehicle.version || ""}${vehicle.year ? ` • ${vehicle.year}` : ""}`}
          </p>

          <div className="mt-5 flex items-end justify-between pt-4 border-t border-[#0B1623]/8">
            <div>
              <p className="font-manrope text-[10px] uppercase tracking-widest text-[#0B1623]/45">
                {isRental ? "Renta Mensual" : (showFromLabel ? "Precio desde" : "Precio")}
              </p>
              <p className={`font-rajdhani font-semibold text-xl md:text-2xl mt-0.5 ${isRental ? "text-[#F09A21]" : "text-[#0B1623]"}`}>
                {priceDisplay}
              </p>
            </div>
            <span className="font-rajdhani font-medium text-sm text-[#0B1623]/70 group-hover:text-[#3B82F6] transition-colors inline-flex items-center gap-1 uppercase tracking-wider">
              Ver más <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
});

export default function VehicleGridPremium({ vehicles, businessSlug = "automotriz", isRental = false, accentColor = "#F09A21", title = "Catálogo", subtitle }) {
  // Si el usuario hizo click en "Ver Catálogo" mientras esta sección aún cargaba,
  // el hash quedó en la URL pero el navegador no encontró el ancla — hacemos el scroll aquí al montarse.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#catalogo") {
      const el = document.getElementById("catalogo");
      if (el) {
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }, []);
  return (
    <section
      id="catalogo"
      className="relative py-16 md:py-24 bg-[#FFFFFF] text-[#0B1623] scroll-mt-16"
      data-testid="vehicle-grid-premium"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <div>
            <span className="font-rajdhani font-medium text-[#3B82F6] text-xs md:text-sm tracking-[0.4em] uppercase">
              {isRental ? "Equipos en renta" : "Modelos disponibles"}
            </span>
            <h2 className="font-rajdhani font-semibold text-[#0B1623] tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-6xl mt-4">
              {title}
            </h2>
            {subtitle && (
              <p className="font-manrope text-[#0B1623]/65 mt-4 max-w-2xl">{subtitle}</p>
            )}
          </div>
          <p className="font-manrope text-[#0B1623]/55 text-sm">
            {vehicles.length} {vehicles.length === 1 ? "resultado" : "resultados"}
          </p>
        </div>

        {vehicles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {vehicles.map((v, i) => (
              <Card
                key={v.id}
                vehicle={v}
                index={i}
                businessSlug={businessSlug}
                isRental={isRental}
                accentColor={accentColor}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-[#0B1623]/10 bg-white p-16 text-center">
            <Car className="w-16 h-16 mx-auto text-[#0B1623]/20 mb-4" />
            <h4 className="font-rajdhani font-semibold text-[#0B1623] text-2xl tracking-tight mb-2">
              Sin resultados
            </h4>
            <p className="font-manrope text-[#0B1623]/55 text-sm">
              Prueba con otra marca o limpia los filtros para ver todo el inventario.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
